import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Check, LayoutGrid, Users, type LucideIcon } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/library/dropdown-menu";
import { cn } from "@/lib/utils";
import { useHrNav } from "@/features/hr/hooks/useHrNav";
import { SidebarHeaderContent } from "./SidebarHeaderContent";

interface SuiteOption {
  key: string;
  label: string;
  home: string;
  Icon: LucideIcon;
}

/**
 * Switches between suites (CRM, HR, later Project Management).
 *
 * It WRAPS the sidebar workspace block ("Hi, <tenant>") as the dropdown
 * trigger rather than adding a row of its own — that block is the only element
 * that survives both the expanded sidebar and the collapsed icon rail, so the
 * switcher stays reachable in both without costing a nav slot.
 *
 * When the user has access to only one suite this renders
 * `SidebarHeaderContent` verbatim: no trigger, no chevron, no hover state. A
 * tenant without HR therefore sees a sidebar pixel-identical to today. That is
 * deliberate — the switcher must not become chrome that every user pays for.
 *
 * The menu is portalled (Radix defaults to `strategy: "fixed"`), which is what
 * keeps it visible despite `body { overflow: clip }` in `styles/base/_base.scss`.
 */
export default function SuiteSwitcher() {
  const navigate = useNavigate();
  const { isHrActive, canAccessHr, homePath } = useHrNav();

  const suites = useMemo<SuiteOption[]>(() => {
    const list: SuiteOption[] = [
      { key: "crm", label: "Xale CRM", home: "/", Icon: LayoutGrid },
    ];
    if (canAccessHr) {
      list.push({ key: "hr", label: "HR", home: homePath, Icon: Users });
    }
    return list;
  }, [canAccessHr, homePath]);

  // Single suite → no switcher chrome at all.
  if (suites.length < 2) return <SidebarHeaderContent />;

  const activeKey = isHrActive ? "hr" : "crm";
  const active = suites.find((s) => s.key === activeKey) ?? suites[0];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="suite-switch-trigger h-full w-full cursor-pointer text-left"
          aria-label={`Current suite: ${active.label}. Switch suite.`}
        >
          <SidebarHeaderContent showSuiteChevron />
        </button>
      </DropdownMenuTrigger>

      {/* Xale control family — same panel/row language as the Reports and
          Dashboard menus (`features/Dashboard/v2/ui/FilterDropdown.tsx`):
          14px radius, #cdd1d0 hairline, soft lift, mint active row.

          Width tracks the trigger, so the panel sits flush under the workspace
          block and spans the sidebar. `min-w` is the floor for the collapsed
          icon rail, where the trigger is only as wide as the logo and an
          equal-width menu would be unreadable. */}
      <DropdownMenuContent
        align="start"
        sideOffset={8}
        className="z-[80] w-(--radix-dropdown-menu-trigger-width) min-w-[218px] rounded-[14px] border border-[#cdd1d0] bg-white p-1 shadow-[0_10px_28px_rgba(30,48,42,0.14)]"
      >
        <p className="px-3 pb-1 pt-1.5 text-[10px] font-medium uppercase tracking-[0.06em] text-[#9ba3a0]">
          Switch suite
        </p>
        {suites.map((suite) => {
          const isActive = suite.key === activeKey;
          return (
            <DropdownMenuItem
              key={suite.key}
              onSelect={() => navigate(suite.home)}
              className={cn(
                "flex h-9 cursor-pointer items-center gap-2.5 rounded-[10px] px-3 text-[13px] transition-colors focus:bg-[#f0f6f3]",
                isActive
                  ? "bg-[#cdeae0] font-medium text-[#051912] focus:bg-[#cdeae0]"
                  : "text-[#374741] hover:bg-[#f0f6f3]"
              )}
            >
              <span
                className={cn(
                  "flex size-6 shrink-0 items-center justify-center rounded-full [&>svg]:size-3.5",
                  isActive
                    ? "bg-white/70 text-[#156548]"
                    : "bg-[#eaf5f0] text-[#218760]"
                )}
              >
                <suite.Icon />
              </span>
              <span className="flex-1 truncate">{suite.label}</span>
              {isActive && (
                <Check className="size-3.5 shrink-0 text-[#218760]" />
              )}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
